import React, { useState, useEffect, useRef, useCallback, useLayoutEffect, useMemo } from 'react';
import { computePosition, autoUpdate, offset, flip, shift } from '@floating-ui/dom';
import cx from 'classnames';
import type { Editor } from '@tiptap/core';
import type { Range } from '@tiptap/core';
import type { SlashMenuItem } from '../types';
import Avatar from '../../../../shared/Avatar';
import shortcutStyles from '../../../views/CreateTask/create_task/description/shortcuts/styles.module.css';
import { ShortcutKeys } from './ToolbarTooltip';
import styles from './SlashCommandPopup.module.css';

export type SlashCommandPopupProps = {
  items: SlashMenuItem[];
  command: (item: SlashMenuItem) => void;
  editor: Editor;
  range: Range;
  query: string;
  decorationNode: Element | null;
  clientRect?: (() => DOMRect | null) | null;
  containerRef?: React.RefObject<HTMLElement | null>;
  keyHandlerRef?: React.MutableRefObject<((e: KeyboardEvent) => void) | null>;
  dismissSuggestion?: () => void;
};

export const SlashCommandPopup = ({
  items,
  command,
  editor,
  range,
  query,
  decorationNode,
  clientRect,
  containerRef,
  keyHandlerRef,
  dismissSuggestion
}: SlashCommandPopupProps) => {
  const [selected, setSelected] = useState(0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [subParent, setSubParent] = useState<SlashMenuItem | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const popupRef = useRef<HTMLDivElement>(null);

  const severityRowClassByPriority: Record<number, string> = {
    0: styles.slashSeverityPriority0,
    1: styles.slashSeverityPriority1,
    2: styles.slashSeverityPriority2,
    3: styles.slashSeverityPriority3,
    4: styles.slashSeverityPriority4
  };

  const hasBack = !!subParent;
  const activeItems = subParent?.subItems ?? items;
  const filteredItems = activeItems;
  const isFiltering = query.trim().length > 0;

  const virtualReference = useMemo(
    () => ({
      getBoundingClientRect: () => {
        if (editor?.view && range) {
          const coords = editor.view.coordsAtPos(range.from);
          const left = coords.left;
          const top = coords.top;
          return new DOMRect(left, top, coords.right - left, coords.bottom - top);
        }
        const fromClient = clientRect?.();
        if (fromClient) {
          return DOMRect.fromRect(fromClient);
        }
        if (decorationNode) {
          return decorationNode.getBoundingClientRect();
        }
        return new DOMRect(0, 0, 0, 0);
      }
    }),
    [editor, range, clientRect, decorationNode]
  );

  const selectItem = useCallback(
    (index: number) => {
      setHoveredIndex(null);
      if (hasBack && index === -1) {
        setSubParent(null);
        setSelected(0);
        return;
      }
      const item = filteredItems[index];
      if (!item) return;
      if (item.subItems && item.subItems.length > 0) {
        setSubParent(item);
        setSelected(0);
      } else {
        command(item);
      }
    },
    [filteredItems, command, hasBack]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (filteredItems.length === 0) return;
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setHoveredIndex(null);
          setSelected(s => Math.min(s + 1, filteredItems.length - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setHoveredIndex(null);
          setSelected(s => Math.max(s - 1, hasBack ? -1 : 0));
          break;
        case 'ArrowRight':
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          setHoveredIndex(null);
          {
            const item = filteredItems[selected];
            if (item?.subItems?.length) {
              setSubParent(item);
              setSelected(0);
            } else {
              selectItem(selected);
            }
          }
          break;
        case 'ArrowLeft':
          e.preventDefault();
          setHoveredIndex(null);
          if (subParent) {
            setSubParent(null);
            setSelected(0);
          }
          break;
        case 'Escape':
          e.preventDefault();
          setHoveredIndex(null);
          if (subParent) {
            setSubParent(null);
            setSelected(0);
          } else {
            dismissSuggestion?.();
          }
          break;
      }
    },
    [filteredItems, selected, selectItem, editor, subParent, hasBack]
  );

  useLayoutEffect(() => {
    if (keyHandlerRef) keyHandlerRef.current = handleKeyDown;
    return () => {
      if (keyHandlerRef) keyHandlerRef.current = null;
    };
  }, [keyHandlerRef, handleKeyDown]);

  useEffect(() => {
    setSelected(0);
    setHoveredIndex(null);
    setSubParent(null);
  }, [query, items]);

  useEffect(() => {
    const maxIdx = filteredItems.length - 1;
    if (selected > maxIdx) setSelected(Math.max(hasBack ? -1 : 0, maxIdx));
    else if (hasBack && selected < -1) setSelected(-1);
  }, [selected, filteredItems.length, hasBack]);

  useEffect(() => {
    if (!listRef.current) return;
    const childIndex = hasBack ? selected + 1 : selected;
    const el = listRef.current.children[childIndex] as HTMLElement;
    el?.scrollIntoView({ block: 'nearest', behavior: 'instant' as ScrollBehavior });
  }, [selected, hasBack]);

  useLayoutEffect(() => {
    const floating = popupRef.current;
    if (!floating || filteredItems.length === 0) return;

    const updatePosition = () => {
      const boundary = containerRef?.current ?? undefined;
      computePosition(virtualReference, floating, {
        placement: 'bottom-start',
        strategy: 'fixed',
        middleware: [
          offset(4),
          flip({ padding: 8, boundary }),
          shift({ padding: 8, boundary })
        ]
      }).then(({ x, y, strategy }) => {
        Object.assign(floating.style, {
          position: strategy,
          left: `${x}px`,
          top: `${y}px`,
          right: 'auto',
          bottom: 'auto',
          zIndex: '10000'
        });
      });
    };

    updatePosition();
    return autoUpdate(virtualReference, floating, updatePosition, {
      ancestorScroll: true,
      ancestorResize: true,
      elementResize: true,
      layoutShift: true
    });
  }, [virtualReference, filteredItems.length, containerRef]);

  if (filteredItems.length === 0) return null;

  return (
    <div
      ref={popupRef}
      className={`${styles.floatingPanel} ${styles.floatingPanelRadiusLg} ${styles.slashPopup}`}
      data-cy="slash-suggestion-popup"
    >
      <div ref={listRef} className={styles.slashList} onMouseLeave={() => setHoveredIndex(null)}>
        {subParent && (
          <div
            className={cx(
              styles.selectableRow,
              styles.selectableRowInteractive,
              styles.slashBackItem,
              hoveredIndex === -1 && styles.selectableRowHovered,
              hoveredIndex == null && selected === -1 && styles.selectableRowSelected
            )}
            onMouseDown={e => {
              e.preventDefault();
              setSubParent(null);
              setSelected(0);
            }}
            onMouseEnter={() => setHoveredIndex(-1)}
          >
            <span className={styles.slashBackArrow}>←</span>
            <span className={styles.slashTitle}>Back</span>
          </div>
        )}
        {filteredItems.map((item, index) => {
          const isTaskRow = !!item.slashTaskRow;
          const isUserRow = !!item.slashUserRow;
          const isSeverityRow = item.slashSeverityRow != null;
          const sev = item.slashSeverityRow?.priority ?? 0;
          const severityClass = severityRowClassByPriority[sev] ?? severityRowClassByPriority[0];
          const isHovered = hoveredIndex === index;
          const isKeyboardSelected = hoveredIndex == null && index === selected;

          const rowClass = isSeverityRow
            ? cx(
                styles.selectableRow,
                styles.selectableRowInteractive,
                styles.slashItemSeverity,
                severityClass,
                isHovered && styles.selectableRowHovered,
                isKeyboardSelected && styles.selectableRowSelected
              )
            : cx(
                styles.selectableRow,
                styles.selectableRowInteractive,
                isHovered && styles.selectableRowHovered,
                isKeyboardSelected && styles.selectableRowSelected
              );

          const rowKey = isUserRow
            ? `${item.slashPeopleAction ?? 'user'}-${item.slashUserRow!.id}`
            : isTaskRow && item.slashTaskRow
              ? `task-${item.slashTaskRow.localTaskId}`
              : isSeverityRow
                ? `severity-${sev}`
                : `${item.title}-${item.subtext ?? ''}-${index}`;
          return (
            <div
              key={rowKey}
              className={rowClass}
              data-cy="slash-suggestion-item"
              onMouseDown={e => {
                e.preventDefault();
                selectItem(index);
              }}
              onMouseEnter={() => setHoveredIndex(index)}
            >
              {isTaskRow && item.slashTaskRow ? (
                <div className={styles.slashTaskLinkRow}>
                  <span
                    className={cx(
                      styles.slashTaskIdBadge,
                      shortcutStyles.localTaskId,
                      shortcutStyles[`priority-${item.slashTaskRow.severity}`]
                    )}
                  >
                    {item.slashTaskRow.localTaskId}
                  </span>
                  <div className={styles.slashContent}>
                    <div className={styles.slashTaskLinkText} title={item.slashTaskRow.labelPlain}>
                      {item.slashTaskRow.labelPlain}
                    </div>
                    {isFiltering && item.subtext && (
                      <div className={styles.slashSubtext}>{item.subtext}</div>
                    )}
                  </div>
                </div>
              ) : isUserRow && item.slashUserRow ? (
                <>
                  <span className={styles.slashAvatarWrap}>
                    <Avatar member={item.slashUserRow} size="default" />
                  </span>
                  <div className={styles.slashContent}>
                    <div className={styles.slashTitle}>{item.title}</div>
                    {item.subtext && (
                      <div className={styles.slashSubtext}>{item.subtext}</div>
                    )}
                  </div>
                </>
              ) : isSeverityRow ? (
                <div className={styles.slashContent}>
                  <div className={styles.slashSeverityTitle}>{item.title}</div>
                  {isFiltering && item.subtext && (
                    <div className={styles.slashSubtext}>{item.subtext}</div>
                  )}
                </div>
              ) : (
                <>
                  {item.icon && (
                    <span className={styles.slashIcon}>{item.icon}</span>
                  )}
                  <div className={styles.slashContent}>
                    <div className={styles.slashTitle}>{item.title}</div>
                    {item.subtext && (!item.slashStatusRow || isFiltering) && (
                      <div className={styles.slashSubtext}>{item.subtext}</div>
                    )}
                  </div>
                  {item.shortcut && item.shortcut.length > 0 && (
                    <span className={styles.slashShortcut}>
                      <ShortcutKeys
                        shortcut={item.shortcut}
                        keyClassName={styles.slashShortcutKey}
                      />
                    </span>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
      <div className={styles.slashDismissHint} data-cy="slash-dismiss-hint">
        <span className={styles.slashShortcutKey}>Esc</span>
        <span>{subParent ? 'to go back' : 'to dismiss'}</span>
      </div>
    </div>
  );
};
